import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Gauge, Info } from "lucide-react";
import { Text } from "@core/index";
import { useTheme } from "@packages/hooks/useTheme";

interface ToleranceLevelSelectorProps {
  value: number;
  onChange: (value: number) => void;
}

const ToleranceLevelSelector = ({
  value,
  onChange,
}: ToleranceLevelSelectorProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [localValue, setLocalValue] = useState(value);
  const [showTooltip, setShowTooltip] = useState(false);

  const toleranceLevels = [
    {
      value: 1,
      label: t("assistant.essayGrader.tolerance.veryStrict"),
      shortLabel: t("assistant.essayGrader.tolerance.veryStrict"),
      description: t("assistant.essayGrader.tolerance.veryStrictDesc"),
      color: "from-red-400 to-red-500",
      bgColor: "bg-red-50 dark:bg-red-900/20",
      textColor: "text-red-700 dark:text-red-300",
      borderColor: "border-red-200 dark:border-red-700",
    },
    {
      value: 2,
      label: t("assistant.essayGrader.tolerance.strict"),
      shortLabel: t("assistant.essayGrader.tolerance.strict"),
      description: t("assistant.essayGrader.tolerance.strictDesc"),
      color: "from-orange-400 to-orange-500",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
      textColor: "text-orange-700 dark:text-orange-300",
      borderColor: "border-orange-200 dark:border-orange-700",
    },
    {
      value: 3,
      label: t("assistant.essayGrader.tolerance.balanced"),
      shortLabel: t("assistant.essayGrader.tolerance.balanced"),
      description: t("assistant.essayGrader.tolerance.balancedDesc"),
      color: "from-blue-400 to-blue-500",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
      textColor: "text-blue-700 dark:text-blue-300",
      borderColor: "border-blue-200 dark:border-blue-700",
    },
    {
      value: 4,
      label: t("assistant.essayGrader.tolerance.lenient"),
      shortLabel: t("assistant.essayGrader.tolerance.lenient"),
      description: t("assistant.essayGrader.tolerance.lenientDesc"),
      color: "from-green-400 to-green-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
      textColor: "text-green-700 dark:text-green-300",
      borderColor: "border-green-200 dark:border-green-700",
    },
    {
      value: 5,
      label: t("assistant.essayGrader.tolerance.veryLenient"),
      shortLabel: t("assistant.essayGrader.tolerance.veryLenient"),
      description: t("assistant.essayGrader.tolerance.veryLenientDesc"),
      color: "from-emerald-400 to-emerald-500",
      bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
      textColor: "text-emerald-700 dark:text-emerald-300",
      borderColor: "border-emerald-200 dark:border-emerald-700",
    },
  ];

  // Function to get the gradient color for each level
  const getGradientColorForLevel = (level: number, theme: "light" | "dark") => {
    const position = (level - 1) / 4; // 0 to 1

    if (position <= 0.5) {
      // Red to Yellow (first half)
      const localPos = position * 2; // 0 to 1 within first half
      const r = Math.round(220 - (220 - 255) * localPos);
      const g = Math.round(38 + (220 - 38) * localPos);
      const b = Math.round(38);

      if (theme === "dark") {
        return {
          bg: `rgb(${r}, ${g}, ${b})`,
          border: `rgb(${Math.round(185 - (185 - 220) * localPos)}, ${Math.round(28 + (185 - 28) * localPos)}, ${Math.round(28)})`,
          shadow: `0 4px 15px rgba(${r}, ${g}, ${b}, 0.3)`,
          text: `rgb(255, 255, 255)`,
          textDark: `rgb(255, 255, 255)`,
          bgLight: `rgba(${r}, ${g}, ${b}, 0.2)`,
          borderLight: `rgba(${r}, ${g}, ${b}, 0.4)`,
        };
      }
      return {
        bg: `rgb(${r}, ${g}, ${b})`,
        border: `rgb(${Math.round(185 - (185 - 220) * localPos)}, ${Math.round(28 + (185 - 28) * localPos)}, ${Math.round(28)})`,
        shadow: `0 4px 15px rgba(${r}, ${g}, ${b}, 0.3)`,
        text: `rgb(${Math.round(r * 0.7)}, ${Math.round(g * 0.7)}, ${Math.round(b * 0.7)})`,
        textDark: `rgb(${Math.round(r * 0.5)}, ${Math.round(g * 0.5)}, ${Math.round(b * 0.5)})`,
        bgLight: `rgba(${r}, ${g}, ${b}, 0.1)`,
        borderLight: `rgba(${r}, ${g}, ${b}, 0.3)`,
      };
    } else {
      // Yellow to Green (second half)
      const localPos = (position - 0.5) * 2; // 0 to 1 within second half
      const r = Math.round(255 - (255 - 34) * localPos);
      const g = Math.round(220 + (197 - 220) * localPos);
      const b = Math.round(38 + (94 - 38) * localPos);
      if (theme === "dark") {
        return {
          bg: `rgb(${r}, ${g}, ${b})`,
          border: `rgb(${Math.round(220 - (220 - 22) * localPos)}, ${Math.round(185 + (163 - 185) * localPos)}, ${Math.round(28 + (74 - 28) * localPos)})`,
          shadow: `0 4px 15px rgba(${r}, ${g}, ${b}, 0.3)`,
          text: `rgb(255, 255, 255)`,
          textDark: `rgb(255, 255, 255)`,
          bgLight: `rgba(${r}, ${g}, ${b}, 0.2)`,
          borderLight: `rgba(${r}, ${g}, ${b}, 0.4)`,
        };
      }
      return {
        bg: `rgb(${r}, ${g}, ${b})`,
        border: `rgb(${Math.round(220 - (220 - 22) * localPos)}, ${Math.round(185 + (163 - 185) * localPos)}, ${Math.round(28 + (74 - 28) * localPos)})`,
        shadow: `0 4px 15px rgba(${r}, ${g}, ${b}, 0.3)`,
        text: `rgb(${Math.round(r * 0.7)}, ${Math.round(g * 0.7)}, ${Math.round(b * 0.7)})`,
        textDark: `rgb(${Math.round(r * 0.5)}, ${Math.round(g * 0.5)}, ${Math.round(b * 0.5)})`,
        bgLight: `rgba(${r}, ${g}, ${b}, 0.1)`,
        borderLight: `rgba(${r}, ${g}, ${b}, 0.3)`,
      };
    }
  };

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleLevelClick = (level: number) => {
    setLocalValue(level);
    onChange(level);
  };

  const getCurrentLevel = () => {
    return (
      toleranceLevels.find((level) => level.value === localValue) ||
      toleranceLevels[2]
    );
  };

  const currentLevel = getCurrentLevel();

  // Check if current level needs a warning
  const needsWarning = localValue === 1 || localValue === 5;
  // Check if current level needs info (levels 2, 3, 4)
  const needsInfo = localValue >= 2 && localValue <= 4;

  return (
    <div className="w-full bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 shadow-xl">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/50 shadow-lg">
            <Gauge className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <Text
              variant="h3"
              size="xl"
              className="font-bold text-gray-800 dark:text-gray-200"
            >
              {t("assistant.essayGrader.tolerance.title")}
            </Text>
            <Text variant="p" size="sm" status="secondary" className="mt-1">
              {t("assistant.essayGrader.tolerance.subtitle")}
            </Text>
          </div>
        </div>

        <div className="relative">
          <button
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Info className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
          {showTooltip && (
            <div className="absolute right-0 top-12 w-80 p-4 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-xl shadow-2xl z-50 border-2 border-gray-600">
              <Text variant="p" size="sm" className="text-white">
                {t("assistant.essayGrader.tolerance.tooltip")}
              </Text>
              <div className="absolute -top-2 right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900 dark:border-b-gray-700"></div>
            </div>
          )}
        </div>
      </div>

      {/* Current Selection Display */}
      <div
        className="relative p-6 rounded-2xl mb-8 border-2 transition-all duration-300 shadow-lg h-48"
        style={{
          backgroundColor: getGradientColorForLevel(localValue, theme).bgLight,
          borderColor: getGradientColorForLevel(localValue, theme).borderLight,
        }}
      >
        {/* Highly visible warning for extreme levels */}
        {needsWarning && (
          <div className="absolute -top-3 -right-3 z-10">
            <div className="bg-red-500 text-white px-3 py-2 rounded-full text-sm font-bold shadow-2xl animate-bounce border-2 border-white">
              ⚠️ {t("assistant.essayGrader.tolerance.warningBadge")}
            </div>
          </div>
        )}

        <div className="flex items-start justify-between h-full">
          <div className="flex-1 flex flex-col h-full">
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-4 h-4 rounded-full shadow-sm"
                style={{
                  backgroundColor: getGradientColorForLevel(localValue, theme)
                    .bg,
                }}
              ></div>
              <Text
                variant="h4"
                className="font-bold text-lg"
                style={{
                  color: getGradientColorForLevel(localValue, theme).textDark,
                }}
              >
                {currentLevel.shortLabel}
              </Text>
            </div>
            <Text
              variant="p"
              size="sm"
              className="opacity-90 mb-3"
              style={{
                color: getGradientColorForLevel(localValue, theme).textDark,
              }}
            >
              {currentLevel.description}
            </Text>

            {/* Content area - benefits for 2,3,4 or warnings for 1,5 */}
            <div className="flex-1">
              {needsInfo && (
                <div className="space-y-1">
                  {localValue === 2 && (
                    <>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.strictBenefits1")}
                      </Text>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.strictBenefits2")}
                      </Text>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.strictBenefits3")}
                      </Text>
                    </>
                  )}
                  {localValue === 3 && (
                    <>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.balancedBenefits1")}
                      </Text>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.balancedBenefits2")}
                      </Text>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.balancedBenefits3")}
                      </Text>
                    </>
                  )}
                  {localValue === 4 && (
                    <>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.lenientBenefits1")}
                      </Text>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.lenientBenefits2")}
                      </Text>
                      <Text
                        variant="p"
                        size="xs"
                        className="opacity-75"
                        style={{
                          color: getGradientColorForLevel(localValue, theme)
                            .textDark,
                        }}
                      >
                        {t("assistant.essayGrader.tolerance.lenientBenefits3")}
                      </Text>
                    </>
                  )}
                </div>
              )}

              {needsWarning && (
                <div className="space-y-2">
                  <Text
                    variant="p"
                    size="sm"
                    className="font-bold"
                    style={{
                      color: getGradientColorForLevel(localValue, theme)
                        .textDark,
                    }}
                  >
                    🚨{" "}
                    {localValue === 1
                      ? t("assistant.essayGrader.tolerance.veryStrictWarning")
                      : t("assistant.essayGrader.tolerance.veryLenientWarning")}
                  </Text>
                  <Text
                    variant="p"
                    size="xs"
                    className="opacity-75"
                    style={{
                      color: getGradientColorForLevel(localValue, theme)
                        .textDark,
                    }}
                  >
                    {localValue === 1
                      ? t("assistant.essayGrader.tolerance.veryStrictImpact")
                      : t("assistant.essayGrader.tolerance.veryLenientImpact")}
                  </Text>
                  <Text
                    variant="p"
                    size="xs"
                    className="opacity-75 font-medium"
                    style={{
                      color: getGradientColorForLevel(localValue, theme)
                        .textDark,
                    }}
                  >
                    💡{" "}
                    <strong>
                      4. {t("assistant.essayGrader.tolerance.level")} (
                      {t("assistant.essayGrader.tolerance.lenient")})
                    </strong>{" "}
                    {t("assistant.essayGrader.tolerance.recommendationText")}
                  </Text>
                </div>
              )}
            </div>
          </div>
          <div
            className="px-5 py-3 rounded-full border-2 shadow-lg self-start"
            style={{
              backgroundColor: getGradientColorForLevel(localValue, theme)
                .bgLight,
              borderColor: getGradientColorForLevel(localValue, theme)
                .borderLight,
            }}
          >
            <Text
              variant="span"
              size="sm"
              className="font-bold"
              style={{
                color: getGradientColorForLevel(localValue, theme).textDark,
              }}
            >
              {t("assistant.essayGrader.tolerance.level")} {localValue}
            </Text>
          </div>
        </div>
      </div>

      {/* Enhanced Precision Slider */}
      <div className="relative mb-8 px-4">
        {/* Track Container */}
        <div className="relative h-10 flex items-center">
          {/* Full Gradient Background - Always Visible */}
          <div className="absolute w-full h-3 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 dark:from-red-600 dark:via-yellow-600 dark:to-green-600 rounded-full shadow-lg"></div>

          {/* Gray Overlay for Inactive Portion (Right Side) */}
          <div
            className="absolute h-3 bg-gray-300/80 dark:bg-gray-600/80 rounded-r-full"
            style={{
              left: `${((localValue - 1) / 4) * 100}%`,
              right: 0,
              transition: "left 0.3s ease-out",
            }}
          ></div>

          {/* Level indicator circles at precise positions */}
          {[1, 2, 3, 4, 5].map((level) => {
            const levelData = toleranceLevels[level - 1];
            const position = ((level - 1) / 4) * 100; // 0%, 25%, 50%, 75%, 100%
            const isActive = level === localValue;
            const gradientColor = getGradientColorForLevel(level, theme);

            return (
              <button
                key={level}
                onClick={() => handleLevelClick(level)}
                className="absolute group transition-all duration-300 hover:scale-110 z-10 focus:outline-none"
                style={{
                  left: `calc(${position}% - 16px)`, // Center the 32px button
                  transform: "translateX(0)", // Ensure exact positioning
                }}
              >
                <div
                  className={`w-8 h-8 rounded-full border-3 transition-all duration-300 ${
                    isActive
                      ? "border-white scale-125 ring-4 ring-white/50"
                      : "border-gray-300 dark:border-gray-500 hover:border-gray-400 hover:shadow-lg"
                  }`}
                  style={{
                    backgroundColor: isActive ? gradientColor.bg : "white",
                    boxShadow: isActive
                      ? gradientColor.shadow
                      : "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                />

                {/* Level number inside circle for inactive states */}
                {!isActive && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-600 dark:text-gray-300">
                      {level}
                    </span>
                  </div>
                )}

                {/* Enhanced hover tooltip */}
                <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 pointer-events-none">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-4 py-2 rounded-xl whitespace-nowrap shadow-2xl border border-gray-600">
                    {levelData.shortLabel}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-6 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                  </div>
                </div>
              </button>
            );
          })}

          {/* Improved range input for proper dragging */}
          <input
            type="range"
            min="1"
            max="5"
            step={1}
            value={localValue}
            onChange={handleSliderChange}
            className="absolute inset-0 w-full h-10 opacity-0 cursor-pointer z-20"
            aria-label={t("assistant.essayGrader.tolerance.title")}
            style={{
              background: "transparent",
              WebkitAppearance: "none",
              appearance: "none",
            }}
          />
        </div>

        {/* Enhanced scale markers - perfectly aligned with circles */}
        <div className="relative mt-4 px-4 h-20">
          {[1, 2, 3, 4, 5].map((level) => {
            const position = ((level - 1) / 4) * 100; // Same calculation as circles
            const levelData = toleranceLevels[level - 1];
            return (
              <div
                key={level}
                className="absolute flex flex-col items-center"
                style={{
                  left: `calc(${position}% - 32px)`, // Wider to accommodate text
                  transform: "translateX(0)", // Ensure exact positioning
                  width: "64px", // Wider for text labels
                }}
              >
                <div className="w-0.5 h-3 bg-gray-400 dark:bg-gray-500 rounded-full mx-auto"></div>
                <Text
                  variant="span"
                  size="xs"
                  className={`mt-2 font-bold transition-all duration-200 text-center leading-tight ${
                    level === localValue
                      ? "text-gray-800 dark:text-white scale-110"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {levelData.shortLabel}
                </Text>
                {/* Compact recommended indicator for level 4 - positioned below text */}
                {level === 4 && (
                  <div className="mt-1 flex justify-center">
                    <div className="bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">
                      <Text
                        variant="span"
                        size="xs"
                        className="text-green-700 dark:text-green-400 font-medium"
                      >
                        ⭐
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ToleranceLevelSelector;
